import { initBirdie } from '@birdie-so/snippet';
import { useAuth } from '@clerk/clerk-react';
import { type Maybe, hasValue, isUUID } from '@vrplatform/utils';
import * as Sentry from '@sentry/react';
import {
  type IdentifyProps,
  type TrackingEvent as LogTrackingEvent,
  type TrackingEventProps,
  type UserRole,
  isTest,
  isVrpMember,
} from '@vrplatform/log';
import { formatUserName } from '@vrplatform/ui-common';
import mixpanel from 'mixpanel-browser';
import { type ReactNode, useCallback, useEffect, useState } from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import {
  GRAPHQL_URI,
  isExtension,
  isLocalhost,
  isProduction,
  isStorybook,
  isStaging,
} from '../env';
import { useGqtyClient, useMutation } from '../graphql';
import { captureSentryError } from './sentry';
import { type TeamType, useMe, useTeam } from './team';
import { IntercomProvider, useIntercom } from './useIntercom';
import {
  isTrackingEnabled,
  shouldDropSentryEvent,
  withUnhandledApiRejectionFingerprint,
  withMaximumUpdateDepthFingerprint,
} from './useTracking.utils';

const TRACKING_ENABLED = isTrackingEnabled({
  isExtension,
  isLocalhost,
  isStorybook,
});
const SENTRY_APPLICATION_KEY = 'vrplatform-ui';

export type Tracking = ReturnType<typeof useTrackingEvent>;
export type TrackingEvent = LogTrackingEvent;

let isSentryInitialized = false;
let isMixpanelInitialized = false;
let isBirdieInitialized = false;

function initSentryOnce() {
  if (!TRACKING_ENABLED || isSentryInitialized) return;
  isSentryInitialized = true;

  Sentry.init({
    dsn: 'https://4ba9b6d2701d4a889e6f323c050a4a46@o515442.ingest.sentry.io/5624987',
    // This sets the sample rate to be 10%. You may want this to be 100% while
    // in development and sample at a lower rate in production
    replaysSessionSampleRate: 1.0,
    // If the entire session is not sampled, use the below sample rate to sample
    // sessions when an error occurs.
    replaysOnErrorSampleRate: 1.0,
    environment: import.meta.env.DEV
      ? 'development'
      : isProduction
        ? 'production'
        : 'staging',
    tracesSampleRate: 1.0,
    tunnel: 'https://pomegranate.vrplatform.app',
    tracePropagationTargets: [
      'localhost:8877',
      'api.vrplatform.app',
      'preview.api.vrplatform.app',
      'staging.api.vrplatform.app',
      'staging.api.edge.vrplatform.app',
      window.location.host,
    ],
    integrations: [
      Sentry.graphqlClientIntegration({
        endpoints: [GRAPHQL_URI],
      }),
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.thirdPartyErrorFilterIntegration({
        filterKeys: [SENTRY_APPLICATION_KEY],
        behaviour: 'drop-error-if-exclusively-contains-third-party-frames',
        ignoreSentryInternalFrames: true,
      }),
      Sentry.replayIntegration({
        maskAllInputs: false,
        maskAllText: false,
        blockAllMedia: false,
        networkDetailAllowUrls: [
          GRAPHQL_URI,
          'https://clerk.vrplatform.app/v1/client/.*',
          'https://cdn.segment.com/v1/projects.*',
          'https://api.vrplatform.app/.*',
          'https://preview.api.vrplatform.app/.*',
          'https://staging.api.vrplatform.app/.*',
          'https://staging.api.edge.vrplatform.app/.*',
          /^https:\/\/preview\.api\.vrplatform\.app\/.*/,
          /^https:\/\/staging\.api\.vrplatform\.app\/.*/,
          /^https:\/\/api\.vrplatform\.app\/.*/,
          /^https:\/\/staging\.api\.edge\.vrplatform\.app\/.*/,
          'https://localhost:8877/.*',
          `${window.location.origin}/trpc/.*`,
          /^https:\/\/portal\.vrplatform\.app\/trpc\/.*/,
          /^https:\/\/staging\.portal\.vrplatform\.app\/trpc\/.*/,
        ],
        networkCaptureBodies: true,
        networkResponseHeaders: ['X-Request-Id'],
        networkRequestHeaders: ['Finalytic-Platform'],
      }),
      isStaging
        ? Sentry.feedbackIntegration({
            colorScheme: 'light',
            showEmail: false,
            showName: false,
            showBranding: false,
            autoInject: false,
            themeLight: {
              submitBackground: '#025FCA',
              submitBackgroundHover: '#024797',
              submitBorder: '#025FCA',
              submitOutlineFocus: '#025FCA',
            },
          })
        : undefined,
    ].filter(hasValue),
    beforeSend: (event, hint) => {
      if (
        shouldDropSentryEvent({
          event,
          hint,
        })
      ) {
        return null;
      }

      return withMaximumUpdateDepthFingerprint(
        withUnhandledApiRejectionFingerprint(event, hint)
      );
    },
  });
}

function initMixpanelOnce() {
  if (!TRACKING_ENABLED || isMixpanelInitialized) return;
  isMixpanelInitialized = true;

  mixpanel.init('933a19cf6d01a51a06ccdd083083f2a9', {
    api_host: 'https://api-eu.mixpanel.com',
    persistence: 'localStorage',
    record_sessions_percent: 1, //records 1% of all sessions
  });
}

function initBirdieOnce() {
  if (!TRACKING_ENABLED || isBirdieInitialized) return;
  isBirdieInitialized = true;

  initBirdie({
    clientId: 'ayjln8yw', // required

    // Optional metadata available to recordings
    // metadata: {
    //   user: {
    //     id: "123",
    //     email: "user@example.com",
    //   },
    // },

    // Optional hook once Birdie is ready
    // onReady(birdie) {
    //   birdie.on("start", (data) => {
    //     birdie.metadata = { dynamicKey: "value" };
    //   });

    //   birdie.on("stop", (data) => {
    //     console.error("Recording stopped", data);
    //   });
    // },
  });
}

const preventTracking = (
  user: {
    id: string;
    email: string;
  },
  tenantId: string | undefined,
  isActor: boolean
): boolean => {
  return (
    isExtension ||
    isTest(user.id, tenantId, user.email) ||
    // isVrpMember(user.id, user.email) ||
    !!isActor
  );
};

function identifySentry(
  args: {
    user: {
      name: string;
      email: string;
      createdAt: string;
      id: string;
    };
    team:
      | {
          id: string;
          name: string;
          isOnboarding: boolean;
          isGL: boolean;
          type: TeamType;
        }
      | undefined;
  } | null
) {
  Sentry.setUser(
    !args
      ? null
      : {
          name: args.user.name,
          email: args.user.email,
          createdAt: args.user.createdAt,
          id: args.user.id,
        }
  );

  Sentry?.setTags({
    // undefined resets a tag
    tenant_id: args?.team?.id,
    tenant_name: args?.team?.name,
    tenant_type: args?.team?.type,
    product: !args?.team
      ? undefined
      : args?.team.isGL
        ? 'VRTrust'
        : 'VRIntegrations',
  });
}

// this boots intercom, mixpanel and sentry at the top level => RootWrapper
export function TrackingInit({ children }: { children: ReactNode }) {
  // moved initSentryOnce() outside useEffect to satisfy the React‑Router v6 requirement that the router wrapper (wrapCreateBrowserRouterV6) runs after Sentry.init. If Sentry initializes later (in an effect), the router gets wrapped before Sentry is ready and you can miss the initial pageload/first navigation. Calling it during render guarantees Sentry is ready before any router is created.
  initSentryOnce();

  useEffect(() => {
    // do not track extension
    if (!TRACKING_ENABLED) return;

    // MIXPANEL
    initMixpanelOnce();

    // BIRDIE
    initBirdieOnce();
  }, []);

  return <IntercomProvider>{children}</IntercomProvider>;
}

export function useTrackingEvent(forceTracking?: boolean) {
  const intercom = useIntercom();
  const [{ id: tenantId }] = useTeam();
  const { actor } = useAuth();
  const { id: meId, email: meEmail } = useMe();
  const gqt = useGqtyClient();

  const { mutate } = useMutation(
    (
      q,
      args: {
        event: string;
        tenantId: string;
        userId: string;
        properties?: Record<string, any>;
      }
    ) =>
      q.insert_tracking_event_one({
        object: {
          name: args.event,
          tenantId: args.tenantId,
          userId: args.userId,
          props: args.properties,
          packageName: 'ui',
        },
      })?.id,
    {
      hideNotification: true,
    }
  );

  const track = useCallback(
    async <TEvent extends LogTrackingEvent>(
      event: TEvent,
      data: {
        userId?: string;
        tenantId?: string;
        userEmail?: string;
      } & TrackingEventProps[TEvent]
    ) => {
      const meta = { userId: meId, tenantId, userEmail: meEmail, ...data };

      if (
        !forceTracking &&
        (isTest(meta.userId, meta.tenantId, meta.userEmail) ||
          isVrpMember(meta.userId, meta.userEmail))
      )
        return;

      let id: string | undefined;

      // only track events in DB for authenticated users
      if (gqt && meId)
        id = await mutate({
          args: {
            event,
            tenantId: meta.tenantId,
            userId: meta.userId,
            properties: meta,
          },
        }).catch((err) => {
          captureSentryError(err);
          return undefined;
        });
      // else
      //   console.warn(
      //     `GQTY client not found. Tracking event ${event} will not work!`,
      //     meta
      //   );

      mixpanel.track(event, {
        $insert_id: id,
        $distinct_id: meId,
        tenant: [meta.tenantId], // assign to group
        ...meta,
      });
      intercom.trackEvent(event, meta);
    },
    [meId, tenantId, meEmail, gqt, mutate, intercom.trackEvent]
  );

  if (
    !forceTracking &&
    preventTracking({ id: meId, email: meEmail }, tenantId, !!actor)
  )
    return async () => {};

  return track;
}

export function useTrackingPage() {
  const intercom = useIntercom();

  return useCallback(() => {
    if (!TRACKING_ENABLED) return;

    const trackPageviewWithRetry = () => {
      try {
        mixpanel.track_pageview();
      } catch (err) {
        // If mixpanel is not ready, retry after a short delay
        console.debug('Mixpanel not ready, retrying in 100ms:', err);
        setTimeout(trackPageviewWithRetry, 100);
      }
    };

    trackPageviewWithRetry();
    intercom.update();
    // sentry tracks page view via wrapping <BrowserRouter>
  }, [intercom.update]);
}

export function useTrackingIdentify() {
  const intercom = useIntercom();
  const { actor } = useAuth();
  const [isBooted, setIsBooted] = useState(false);

  return useCallback(
    ({
      intercomIdentifyId,
      team,
      user,
    }: {
      intercomIdentifyId: string;
      user: Required<
        NonNullable<IdentifyProps['traits']> & {
          id: string;
          email: string;
          lastName: Maybe<string>;
          firstName: Maybe<string>;
          avatar: Maybe<string>;
          role: Maybe<UserRole>;
          country: Maybe<string>;
        }
      >;
      team:
        | {
            id: string;
            name: string;
            isOnboarding: boolean;
            isGL: boolean;
            type: TeamType;
          }
        | undefined;
    }) => {
      if (isBooted) return;
      setIsBooted(true);

      if (
        !TRACKING_ENABLED ||
        preventTracking({ id: user.id, email: user.email }, team?.id, !!actor)
      )
        return;

      const {
        id: userId,
        email,
        avatar,
        firstName,
        lastName,
        createdAt,
        lastSeenAt,
        ...traits
      } = user;

      const name = formatUserName(
        {
          firstName,
          lastName,
        },
        { lastNameFirst: false, showEmpty: true }
      );

      // MIXPANEL
      try {
        mixpanel.identify(userId);
        mixpanel.people.set({
          $distinct_id: userId,
          $name: name,
          $first_name: firstName,
          $last_name: lastName,
          $email: email,
          $avatar: avatar,
          $last_seen: lastSeenAt,
          $created: createdAt,
          ...traits,
        });

        if (team) mixpanel.set_group('tenant', team.id);
      } catch (err) {
        console.error('Error identifying mixpanel:', err);
      }

      // SENTRY
      identifySentry({
        user,
        team,
      });

      // Wait for Intercom to boot (max 20 seconds)
      const timeout = setTimeout(() => {
        clearInterval(interval);
        console.warn('Intercom boot timeout');
      }, 20000);

      const interval = setInterval(() => {
        if ((window as any)?.Intercom?.booted) {
          intercom.boot({
            // data attributes
            userId: user.id,
            userHash: intercomIdentifyId,
            // messenger attributes
            hideDefaultLauncher: !!team && !team?.isOnboarding,
            customLauncherSelector: '#intercom-widget',
            alignment: 'right',
            horizontalPadding: 40,
            verticalPadding: 40,
            name,
            createdAt,
            avatar: { imageUrl: avatar, type: 'avatar' },
            customAttributes: {
              userType: traits.role,
              teamId: team?.id,
              team: team?.name,
            },
          });

          // Clear interval after successful boot
          clearInterval(interval);
          clearTimeout(timeout);
        }
      }, 1000);

      return () => {
        clearInterval(interval);
        clearTimeout(timeout);
      };
    },
    [intercom.boot, actor, isBooted]
  );
}

export function useTrackingReset() {
  const intercom = useIntercom();

  if (!TRACKING_ENABLED)
    return async (signOut: (() => Promise<void>) | undefined) => {
      if (signOut) await signOut();
    };

  return useCallback(
    async (signOut: (() => Promise<void>) | undefined) => {
      // mixpanel
      let isMixpanelIdentified = false;
      try {
        const distinctId = mixpanel.get_distinct_id();
        isMixpanelIdentified = isUUID(distinctId);
      } catch (err) {
        console.error('Error getting mixpanel distinct_id:', err);
        isMixpanelIdentified = false;
      }
      if (isMixpanelIdentified) mixpanel.reset();

      // sentry
      identifySentry(null);

      // only reset intercom if we are signing out
      if (signOut) await signOut();
      intercom.boot({}); // boot intercom without user
    },
    [intercom.boot]
  );
}

export function useTrackingSignOut() {
  const me = useMe();
  const { signOut: clerkSignOut } = useAuth();
  const track = useTrackingEvent();
  const reset = useTrackingReset();

  return useCallback(
    async ({ trackEvent }: { trackEvent: boolean }) => {
      if (!!TRACKING_ENABLED && trackEvent) {
        await track('account_signed_out', {
          userEmail: me.email,
          lastName: me.lastName,
          firstName: me.firstName,
        })
          .catch((err) => {
            captureSentryError(err);
          })
          .finally(() => {
            reset(clerkSignOut);
          });
      } else await reset(clerkSignOut);
    },
    [clerkSignOut, reset, track, me.email, me.lastName, me.firstName]
  );
}
